import React from 'react';
import { useParams } from '@tanstack/react-router';
import { SurveyModel } from 'survey-core';
import Survey from '../survey/survey';
import { getSurveyModel } from '../survey-questions/get-survey-model';
import { ThirdPartyLoaderResponse } from '../../utils/third-party-loader';
import {
  ApiError, isApiError, isGoneError,
} from '../../utils/errors'; import { useStatusMessageDispatch } from '../../hooks/StatusMessageContext';
import ErrorPage from '../status-pages/error-page';
import { postThirdParty } from '../../api/third-parties/post-third-party';
import { ThirdPartySurvey, ThirdPartyType } from '../../types';
import i18n from '../../i18n';

interface ThirdPartyPageProps {
  loaderData: ThirdPartyLoaderResponse;
  routeFrom: string;
}

const ThirdPartyPage: React.FC<ThirdPartyPageProps> = ({ loaderData, routeFrom }) => {
  const [ goneError, setGoneError ] = React.useState<ApiError | null>(null);
  const { setStatusMessage } = useStatusMessageDispatch();
  const { trackingId } = useParams({ from: routeFrom }) as { trackingId: string };

  if (loaderData.type === 'error') {
    return <ErrorPage errorType={loaderData.error} />;
  }

  if (goneError) {
    return <ErrorPage errorType={goneError} />;
  }

  // TypeScript now knows this is the success case, so settings is guaranteed to exist
  const { settings } = loaderData;

  const thirdPartySurvey = settings.survey as ThirdPartySurvey;
  const isPersonalGuarantor = thirdPartySurvey.type === ThirdPartyType.PERSONAL_GUARANTOR;
  const completionMessageKey = isPersonalGuarantor
    ? 'components:pages.personal_guarantor_submitted'
    : 'components:pages.trade_reference_submitted';
  const successMessageKey = isPersonalGuarantor
    ? 'components:pages.personal_guarantor_success'
    : 'components:pages.trade_reference_success';
  const errorMessageKey = isPersonalGuarantor
    ? 'components:pages.personal_guarantor_error'
    : 'components:pages.trade_reference_error';

  const handleSubmission = async (sender: SurveyModel) => {
    try {
      await postThirdParty(settings.urlSlug, trackingId, sender.data);

      setStatusMessage({
        severity: 'success',
        message: i18n.t(successMessageKey),
      });
    } catch (err) {
      if (isGoneError(err)) {
        setGoneError(err);
        return;
      }
      setStatusMessage({
        severity: 'error',
        message: isApiError(err) ? err.message : i18n.t(errorMessageKey),
      });
    }
  };

  const surveyModel = getSurveyModel({
    settings,
    onComplete: handleSubmission,
    completedHtml: i18n.t(completionMessageKey),
    trackingId,
  });

  return <Survey surveyModel={surveyModel} />;
};

export default ThirdPartyPage;
